/**
 * Small, fast, seedable PRNG (sfc32) with serializable state.
 *
 * The engine never reads a clock or Math.random(): every shuffle comes from the
 * RNG state stored in the game, so a game can be replayed exactly from its seed.
 * The server seeds each game from a cryptographically secure source and never
 * sends the seed or RNG state to clients.
 */

export type RngState = [number, number, number, number];

export function seedRng(seed: string | number): RngState {
  // xmur3 string hash to spread the seed across 4 words.
  const str = String(seed);
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  const next = () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
  const state: RngState = [next(), next(), next(), next()];
  // Warm up.
  for (let i = 0; i < 15; i++) nextUint32(state);
  return state;
}

/** Advances `state` in place and returns a uint32. */
export function nextUint32(state: RngState): number {
  let [a, b, c, d] = state;
  a >>>= 0;
  b >>>= 0;
  c >>>= 0;
  d >>>= 0;
  let t = (a + b) | 0;
  a = b ^ (b >>> 9);
  b = (c + (c << 3)) | 0;
  c = (c << 21) | (c >>> 11);
  d = (d + 1) | 0;
  t = (t + d) | 0;
  c = (c + t) | 0;
  state[0] = a;
  state[1] = b;
  state[2] = c;
  state[3] = d;
  return t >>> 0;
}

/** Unbiased integer in [0, n). */
export function randomInt(state: RngState, n: number): number {
  if (!Number.isInteger(n) || n <= 0) throw new Error(`randomInt: bad bound ${n}`);
  const limit = 0x100000000 - (0x100000000 % n);
  let x: number;
  do {
    x = nextUint32(state);
  } while (x >= limit);
  return x % n;
}

/** Fisher–Yates shuffle, in place. */
export function shuffleInPlace<T>(state: RngState, arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = randomInt(state, i + 1);
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  return arr;
}

/** Convenience wrapper for code (like bots) that wants an object. */
export class Rng {
  readonly state: RngState;
  constructor(seed: string | number | RngState) {
    this.state = Array.isArray(seed) ? ([...seed] as RngState) : seedRng(seed);
  }
  int(n: number): number {
    return randomInt(this.state, n);
  }
  float(): number {
    return nextUint32(this.state) / 0x100000000;
  }
  shuffle<T>(arr: T[]): T[] {
    return shuffleInPlace(this.state, arr);
  }
  pick<T>(arr: readonly T[]): T {
    if (arr.length === 0) throw new Error('pick from empty array');
    return arr[this.int(arr.length)]!;
  }
}
